import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import styled from "styled-components";

import {
  currencySignSelector,
  DashboardCardNameEnum,
  DashboardCardTemplateTypeEnum,
  DATA_E2E_KEY,
  INumericComparisonCardContent,
  INumericComparisonCardSettings,
  IStyledAttributes,
  useFormats,
} from "@ryde/shared";
import { rydeColors, rydeColorsDashboardPage } from "@ryde/theme";

import { CardNumericComparisonContentSubheaderTemplate } from "./dashboard.card.numeric.comparison-subheader.template";

export const DashboardCardNumericComparisonContentMainStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "dashboard-numeric-card-content",
  },
)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-areas: "value description";
  background-color: ${rydeColors.white};
  align-content: center;
  height: 70%;
  align-items: center;
`;

export const DashboardCardNumericComparisonValueTodayStyled = styled.div`
  font-size: 48px;
  border-inline-end: 3px solid ${rydeColorsDashboardPage.numericSeparatorColor};
  padding: 10px;
  align-content: center;
  grid-area: value;
  text-align: center;
`;

export const DashboardCardNumericComparisonDescriptionStyled = styled.div`
  padding: 20px;
  font-size: 18px;
  grid-area: description;
`;

export interface INumericComparisonContentProps {
  cardType: DashboardCardTemplateTypeEnum;
  cardName: DashboardCardNameEnum;
  content: INumericComparisonCardContent;
  settings: INumericComparisonCardSettings;
  description?: string;
  extension?: string;
}

export const CardNumericComparisonContentTemplate: FC<
  INumericComparisonContentProps
> = ({
  cardName,
  cardType = DashboardCardTemplateTypeEnum.NUMERIC_COMPARISON,
  content,
  settings,
  description: descriptionOverride,
  extension: extensionOverride,
}) => {
  const { t } = useTranslation();
  const { getNumberWithCurrencyNormalized, getNumberWithCommasNormalized } =
    useFormats();
  const currencySign = useRecoilValue(currencySignSelector);

  const [extension, description] = [
    extensionOverride ??
      (cardName === DashboardCardNameEnum.OVERALL_COST
        ? currencySign
        : t(`dashboard.${cardName}.EXTENSION`)),
    descriptionOverride ?? t(`dashboard.${cardName}.DESCRIPTION`),
  ];

  const valueTodayWithCommas = getNumberWithCommasNormalized(
    content.valueToday,
  );
  return (
    <>
      {settings.hasSubheader && (
        <CardNumericComparisonContentSubheaderTemplate
          cardName={cardName}
          cardType={cardType}
          settings={settings}
          content={{
            valueLastWeek: content.valueLastWeek ?? 0,
            valueToday: content.valueToday,
          }}
        />
      )}
      {!settings.hasSubheader && <div style={{ height: "15%" }} />}
      <DashboardCardNumericComparisonContentMainStyled>
        <DashboardCardNumericComparisonValueTodayStyled>
          {getNumberWithCurrencyNormalized(valueTodayWithCommas, extension)}
        </DashboardCardNumericComparisonValueTodayStyled>
        <DashboardCardNumericComparisonDescriptionStyled>
          {description}
        </DashboardCardNumericComparisonDescriptionStyled>
      </DashboardCardNumericComparisonContentMainStyled>
    </>
  );
};
